import { MoreHorizontal } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/shared/lib/utils'
import { Button, type ButtonProps } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/shared/ui/dropdown-menu'

/** Una acción de panel. Portable: no conoce nada específico de la app. */
export interface SidePanelAction {
  label: string
  onClick: () => void
  variant?: ButtonProps['variant']
  icon?: React.ReactNode
  disabled?: boolean
  /**
   * Por qué la acción está deshabilitada, cuando la persona no lo puede deducir sola (p. ej.
   * «Disponible cuando la app esté publicada»). Sólo se muestra con `disabled`: va DEBAJO del
   * botón y no en un tooltip, porque un botón deshabilitado no recibe el foco ni el puntero y se
   * llevaría la explicación con él.
   */
  hint?: string
  /** Acción principal: va arriba, siempre visible. Por defecto, la última del arreglo. */
  primary?: boolean
}

export interface SidePanelActionsProps extends React.ComponentPropsWithoutRef<'div'> {
  actions: SidePanelAction[]
  /** Máximo de acciones visibles como botón antes de colapsar las secundarias al menú. */
  maxInline?: number
  /** Ícono del disparador del menú de overflow. */
  overflowIcon?: React.ReactNode
  /** Texto del disparador de overflow (y label accesible). */
  overflowLabel?: string
}

/** Índice de la acción principal: la marcada con `primary`, o la última por convención. */
function resolvePrimaryIndex(actions: SidePanelAction[]): number {
  const marked = actions.findIndex((action) => action.primary)
  return marked >= 0 ? marked : actions.length - 1
}

function resolveVisibleSecondaryCount(maxInline: number): number {
  return Math.max(0, maxInline - 1)
}

function ActionButton({ label, onClick, variant, icon, disabled, hint }: SidePanelAction) {
  const button = (
    <Button
      type="button"
      variant={variant ?? 'outline'}
      disabled={disabled}
      onClick={onClick}
      className="w-full gap-1.5"
    >
      {icon}
      {label}
    </Button>
  )

  // Sin hint el árbol es el de siempre: el wrapper sólo existe cuando hay algo que explicar.
  if (!disabled || !hint) return button

  return (
    <div className="flex flex-col gap-1">
      {button}
      <p className="text-center text-muted-foreground text-xs">{hint}</p>
    </div>
  )
}

function OverflowMenu({
  actions,
  overflowIcon,
  overflowLabel
}: {
  actions: SidePanelAction[]
  overflowIcon: React.ReactNode
  overflowLabel: string
}) {
  if (actions.length === 0) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-10 w-10 shrink-0"
          aria-label={overflowLabel}
        >
          {overflowIcon}
          <span className="sr-only">{overflowLabel}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="end" className="w-48">
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.label}
            disabled={action.disabled}
            title={action.disabled ? action.hint : undefined}
            onSelect={() => {
              action.onClick()
            }}
            className={cn(
              'gap-2',
              action.variant === 'destructive' && 'text-red-600 focus:text-red-600'
            )}
          >
            {action.icon}
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Barra de acciones estándar de un panel/diálogo.
 *
 * Layout: botones **apilados a ancho completo, la acción principal arriba** (sólida) y las
 * secundarias debajo (outline). Puede vivir dentro del contenido de un detalle o en un footer
 * fijo cuando el flujo lo requiera. El overflow pertenece a la fila secundaria: si hay más de
 * `maxInline` (2 por defecto) acciones, las secundarias sobrantes colapsan en un botón
 * "⋯ Más" al lado de la primera secundaria. La principal es la marcada con `primary` o,
 * si no, la última del arreglo.
 *
 * Reutilizable: sólo depende de primitivos shadcn (Button, DropdownMenu) y de `cn`. No sabe
 * nada del dominio; las acciones se inyectan desde afuera.
 */
export const SidePanelActions = React.forwardRef<HTMLDivElement, SidePanelActionsProps>(
  (
    {
      actions,
      maxInline = 2,
      overflowIcon = <MoreHorizontal className="h-4 w-4" />,
      overflowLabel = 'Más acciones',
      className,
      ...props
    },
    ref
  ) => {
    if (actions.length === 0) return null

    const primaryIndex = resolvePrimaryIndex(actions)
    const primary = actions[primaryIndex]
    const secondary = actions.filter((_, index) => index !== primaryIndex)
    const visibleSecondaryCount = resolveVisibleSecondaryCount(maxInline)
    const inlineSecondary = secondary.slice(0, visibleSecondaryCount)
    const overflowSecondary = secondary.slice(visibleSecondaryCount)

    const [firstSecondary, ...remainingSecondary] = inlineSecondary

    return (
      <div ref={ref} className={cn('flex flex-col gap-2', className)} {...props}>
        <ActionButton {...primary} variant={primary.variant ?? 'default'} />

        {(firstSecondary || overflowSecondary.length > 0) && (
          <div className="flex gap-2">
            {firstSecondary && (
              <div className="min-w-0 flex-1">
                <ActionButton {...firstSecondary} />
              </div>
            )}
            <OverflowMenu
              actions={overflowSecondary}
              overflowIcon={overflowIcon}
              overflowLabel={overflowLabel}
            />
          </div>
        )}

        {remainingSecondary.map((action) => (
          <ActionButton key={action.label} {...action} />
        ))}
      </div>
    )
  }
)
SidePanelActions.displayName = 'SidePanelActions'
